import QuizStatusBadge from 'components/badge/QuizStatusBadge';
import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { DivConst } from 'constants/divConst';
import { useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import WsReportAttributeDistribution from 'pages/ws/components/reportAttributeDistribution/WsReportAttributeDistribution';
import WsReportBehaviorAnalytics from 'pages/ws/components/reportBehaviorAnalytics/WsReportBehaviorAnalytics';
import WsReportChart from 'pages/ws/components/reportChart/WsReportChart';
import WsReportResultDistribution from 'pages/ws/components/reportResultDistribution/WsReportResultDistribution';
import WsReportAnswerCombinationRanking from 'pages/ws/components/reportAnswerCombinationRanking/WsReportAnswerCombinationRanking';
import WsReportQuestionAnswer from 'pages/ws/components/reportQuestionAnswer/WsReportQuestionAnswer';
import WsModalReportDownload from 'pages/ws/components/modalReportDownload/WsModalReportDownload';
import WsModalReportTerm from 'pages/ws/components/modalReportTerm/WsModalReportTerm';
import WsReportDetailHeader from 'pages/ws/components/reportDetailHeader/WsReportDetailHeader';
import WsReportTermSection from 'pages/ws/components/reportTermSection/WsReportTermSection';
import WsReportMetrics from 'pages/ws/components/reportMetrics/WsReportMetrics';
import { useWsReportQuiz } from './hooks/useWsReportQuiz';

import type { JSX } from 'react';

/**
 * クイズレポート詳細画面
 */
export default function WsReportQuiz(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const navigate = useNavigate();
  const { quizCd } = useParams();

  const [acctState] = useAtom(AcctState);

  const {
    initialized,
    processing,
    quiz,
    reportDateFrom,
    reportDateTo,
    isExpiredPlan,
    modalTermOpen,
    setModalTermOpen,
    modalDownloadOpen,
    setModalDownloadOpen,
    onContentOpen,
    onModalReportDownloadOpen,
    onModalReportTermOpen,
    onChangeReportTerm,
  } = useWsReportQuiz(quizCd, acctState.company);

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* 初期化中の表示 */}
      <IndicatorScreen visible={!initialized || processing} />
      {/* 初期化後の表示 */}
      {initialized && quiz && (
        <>
          <WsReportDetailHeader
            imageKey={quiz.imageKey}
            title={quiz.title}
            createdAt={quiz.createdAt}
            statusBadge={<QuizStatusBadge quizStatusDiv={quiz.quizStatusDiv} />}
            isExpiredPlan={isExpiredPlan}
            onBack={() => navigate(-1)}
            onContentOpen={onContentOpen}
            onModalReportDownloadOpen={onModalReportDownloadOpen}
          />

          <WsReportTermSection
            reportDateFrom={reportDateFrom}
            reportDateTo={reportDateTo}
            onModalReportTermOpen={onModalReportTermOpen}
          />

          <WsReportMetrics
            participatedCount={quiz.participatedCount}
            lineFlag={quiz.lineFlag}
            mailAddressCount={quiz.mailAddressCount}
            lineCount={quiz.lineCount}
            mailAddressAcquisitionRate={quiz.mailAddressAcquisitionRate}
            lineAddressAcquisitionRate={quiz.lineAddressAcquisitionRate}
            answeredCount={quiz.answeredCount}
            answeredRate={quiz.answeredRate}
          />

          <WsReportChart reports={quiz.reports} lineFlag={quiz.lineFlag} />

          <WsReportBehaviorAnalytics behaviorAnalytics={quiz.behaviorAnalytics} />

          {/* 正答率の分布・属性分布・設問別回答は参加者がいる場合のみ表示 */}
          {quiz.participatedCount > 0 && (
            <>
              <section className="section-reports-top">
                <div className="container">
                  <div className="row">
                    <div className="col-lg-6 section-reports-left-col">
                      {quiz.resultDistribution && quiz.resultDistribution.length > 0 && (
                        <WsReportResultDistribution
                          resultDistribution={quiz.resultDistribution}
                          title="正答率の分布"
                        />
                      )}
                    </div>
                    <div className="col-lg-6 section-reports-right-col">
                      {quiz.attributeDistribution &&
                        (quiz.attributeDistribution.gender.length > 0 ||
                          quiz.attributeDistribution.ageGroup.length > 0 ||
                          quiz.attributeDistribution.prefecture.length > 0) && (
                          <WsReportAttributeDistribution
                            attributeDistribution={quiz.attributeDistribution}
                          />
                        )}
                    </div>
                  </div>
                  {Array.isArray(quiz.questionAnswerDistribution) &&
                    quiz.questionAnswerDistribution.length > 0 && (
                      <div className="row row--question-answer">
                        <div className="col-12">
                          <WsReportQuestionAnswer
                            questionAnswerDistribution={quiz.questionAnswerDistribution}
                          />
                        </div>
                      </div>
                    )}
                </div>
              </section>

              {/* 回答パターンランキング（質問別回答選択パターンと別カード） */}
              {Array.isArray(quiz.answerCombinationRanking) &&
                quiz.answerCombinationRanking.length > 0 && (
                  <section className="section-reports-top section-reports-top--answer-ranking">
                    <div className="container">
                      <div className="row">
                        <div className="col-12">
                          <WsReportAnswerCombinationRanking
                            answerCombinationRanking={quiz.answerCombinationRanking}
                            questionAnswerDistribution={quiz.questionAnswerDistribution}
                          />
                        </div>
                      </div>
                    </div>
                  </section>
                )}
            </>
          )}
        </>
      )}

      {/* レポート期間設定モーダル */}
      <WsModalReportTerm
        open={modalTermOpen}
        setOpen={setModalTermOpen}
        reportDateFrom={reportDateFrom}
        reportDateTo={reportDateTo}
        onSubmit={attr => onChangeReportTerm(attr.reportDateFrom, attr.reportDateTo)}
      />
      {/* レポートダウンロードモーダル */}
      <WsModalReportDownload
        open={modalDownloadOpen}
        setOpen={setModalDownloadOpen}
        cd={quizCd}
        contentsType={DivConst.items.contentsType.quiz.value}
        reportDateFrom={null}
        reportDateTo={null}
      />
      {
        // プラン（利用期間切れ）の場合に表示する
        isExpiredPlan && <Tooltip id="custom-tooltip" place="top" variant="info" />
      }
    </>
  );
}
